import { z } from 'zod';
import { operatorApiFetch } from './apiService';
import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

// Schema mirrors Api::Operator::V1::InventoryTypeReclassifyRulesController#serialize
// (app/controllers/api/operator/v1/inventory_type_reclassify_rules_controller.rb:69-101).
// Operator endpoint is READ-ONLY: index + show only. Tier fields & rule fields are
// identical shape to admin but the controller enforces `merchant_id` query param +
// `current_operator.merchants` scoping.
//
// 同 admin schema：threshold 為下限（含），ratio 為 0~100 百分比。
// All object schemas use .passthrough() so backend additions don't break parsing.

const TierSchema = z.object({
  id: z.string().optional(),
  original_expiration_date_threshold: z.number().int().nonnegative(),
  trigger_mechanism: z.enum([ 'by_days', 'by_ratio' ]),
  days_before_expiration: z.number().int().positive().nullable(),
  expiration_ratio: z.number().positive().max(100).nullable(),
}).passthrough();

const InventoryTypeMiniSchema = z.object({
  id: z.string(),
  name: z.string(),
}).passthrough();

const OwnerSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
}).passthrough();

export const OperatorRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  priority: z.number().int(),
  enabled: z.boolean(),
  source_inventory_type_id: z.string(),
  target_inventory_type_id: z.string(),
  source_inventory_type: InventoryTypeMiniSchema.optional().nullable(),
  target_inventory_type: InventoryTypeMiniSchema.optional().nullable(),
  owner_merchant_user: OwnerSchema.optional().nullable(),
  tiers: z.array(TierSchema),
}).passthrough();

export type OperatorRule = z.infer<typeof OperatorRuleSchema>;
export type OperatorRuleTier = z.infer<typeof TierSchema>;

// Controller#index uses render_array_list — the envelope is the standard list
// shape `{ success, message, data: [...], has_more: false }`, so callers unwrap
// via `result.data` (not `result.data.data`).
export const operatorInventoryTypeReclassifyRuleService = {
  index: async (merchantId: string) => {
    const res = await operatorApiFetch('inventory_type_reclassify_rules', {
      params: { merchant_id: merchantId },
    });
    return parseApiResponse(res, apiList(OperatorRuleSchema));
  },

  show: async (id: string, merchantId: string) => {
    const res = await operatorApiFetch(`inventory_type_reclassify_rules/${id}`, {
      params: { merchant_id: merchantId },
    });
    return parseApiResponse(res, apiSuccess(OperatorRuleSchema));
  },
};

export default operatorInventoryTypeReclassifyRuleService;
